// src/stores/vocabulary.js
//
// PRD-002 estabilização (2026-05-12) — store Pinia do vocabulário de domínio
// do regula-hub. Cacheia labels/descriptions/variants/icons no boot pra que
// `useRegulaHubLabels()` e tooltips/modals consultem sem round-trip.
//
// Estratégia de cache em 2 camadas:
//   1) Memória (Pinia state) — usado durante a sessão atual.
//   2) localStorage — sobrevive a refresh; permite render imediato no boot
//      enquanto o fetch valida via If-None-Match.
//
// Cold boot fluxo:
//   a) Lê localStorage → state populado (instant).
//   b) Dispara fetch com If-None-Match=<etag salvo>.
//   c) Se 304, mantém. Se 200, atualiza state + localStorage.
//   d) Se falhar, mantém o stale (graceful degradation — UI sobrevive a
//      hub offline mostrando labels antigos).

import { defineStore } from "pinia";
import { fetchVocabulary } from "@/services/vocabularyService";
import { logger } from "@/utils/logger";

const STORAGE_KEY = "regulaHub.vocabulary.v1";

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    logger.warn("vocabulary localStorage parse failed:", err);
    return null;
  }
}

function writeStorage(snapshot) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch (err) {
    // Quota / private mode — degrada silenciosamente, mantém memória.
    logger.warn("vocabulary localStorage write failed:", err);
  }
}

export const useVocabularyStore = defineStore("vocabulary", {
  state: () => ({
    lang: "pt-BR",
    version: "",
    etag: "",
    statuses: {},
    failureReasons: {},
    integrationSubstatuses: {},
    creationWarnings: {},

    loaded: false,
    loading: false,
    error: null,
  }),

  getters: {
    /** Acesso uniforme pra qualquer categoria. Útil pra fallback. */
    all(state) {
      return {
        statuses: state.statuses,
        failureReasons: state.failureReasons,
        integrationSubstatuses: state.integrationSubstatuses,
        creationWarnings: state.creationWarnings,
      };
    },
  },

  actions: {
    /**
     * Hidrata o store. Chame uma vez no boot da app (em `main.js`).
     *
     * - Se há cópia em localStorage, popula o state imediatamente.
     * - Dispara fetch em paralelo (não bloqueia render). Se 304, ok.
     *   Se 200, atualiza. Se falhar, mantém stale.
     * - Idempotente — chamadas subsequentes só re-fetcham com If-None-Match.
     */
    async load({ lang = "pt-BR" } = {}) {
      // 1) Cold-boot: tenta localStorage primeiro.
      if (!this.loaded) {
        const cached = readStorage();
        if (cached && cached.lang === lang) {
          this.applySnapshot(cached);
          this.loaded = true; // já tem algo utilizável
        }
      }

      // 2) Fetch (com If-None-Match se já temos ETag).
      this.loading = true;
      this.error = null;
      try {
        const result = await fetchVocabulary({ lang, etag: this.etag || null });
        if (result.notModified) {
          // Backend confirmou que a versão cacheada está fresh — nada a fazer.
          return;
        }
        // 200 com payload novo.
        const snapshot = {
          lang: result.data.lang,
          version: result.data.version,
          etag: result.etag || "",
          statuses: result.data.statuses || {},
          failureReasons: result.data.failure_reasons || {},
          integrationSubstatuses: result.data.integration_substatuses || {},
          creationWarnings: result.data.creation_warnings || {},
        };
        this.applySnapshot(snapshot);
        this.loaded = true;
        writeStorage(snapshot);
      } catch (err) {
        // Degradação graciosa: se não temos NADA no state e falhou, deixa
        // marcado pra que consumers exibam a chave crua (fallback). Se já
        // temos algo do localStorage, mantém.
        this.error = err.message || String(err);
        logger.error("vocabulary load failed (using stale if any):", err);
      } finally {
        this.loading = false;
      }
    },

    /** Força refetch (ignora ETag). Útil pra tela admin de "Recarregar". */
    async forceRefresh({ lang = "pt-BR" } = {}) {
      this.etag = "";
      await this.load({ lang });
    },

    applySnapshot(snapshot) {
      this.lang = snapshot.lang;
      this.version = snapshot.version;
      this.etag = snapshot.etag;
      this.statuses = snapshot.statuses;
      this.failureReasons = snapshot.failureReasons;
      this.integrationSubstatuses = snapshot.integrationSubstatuses;
      this.creationWarnings = snapshot.creationWarnings;
    },
  },
});
